import {createContext, useContext, useMemo, useState} from 'react'

/**
 * Height of the impersonation ("Acting as") banner, so viewport-pinned chrome
 * can shift down instead of being overlapped by it. The banner is web-only and
 * sticky at the very top of the shell: elements positioned with
 * `fixed`/`sticky` `top: 0` sit in the same viewport spot and end up UNDER the
 * banner (it deliberately wins the z-order — it must stay unmissable), hiding
 * real page tools. Consumers read this offset as their `top` instead of 0.
 * Always 0 on native and whenever no impersonation session is active — the
 * banner sets it from onLayout and resets it on unmount.
 */

type StateContext = {
  offset: number
  setOffset: (n: number) => void
}

const Context = createContext<StateContext>({
  offset: 0,
  setOffset: () => {},
})
Context.displayName = 'ImpersonationOffsetContext'

export function Provider({children}: React.PropsWithChildren<{}>) {
  const [offset, setOffset] = useState(0)
  const value = useMemo(() => ({offset, setOffset}), [offset])
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useImpersonationOffset(): number {
  return useContext(Context).offset
}

export function useSetImpersonationOffset(): (n: number) => void {
  return useContext(Context).setOffset
}
